import { JsonSchemaUtils, Objects, SitemapNode, SitemapVisibility } from "@salaxy/core";
import { SitemapHelper } from "./SitemapHelper";

/** Contains information about the current sitemap node (if resoved) and other data related to route. */
export class CurrentRoute {
  constructor(currentPath: SitemapNode[]) {
    if (currentPath?.length) {
      this.isResolved = true;
      this.path = currentPath;
      this.node = currentPath[currentPath.length - 1],
      this.section = currentPath[0];
    } else {
      this.isResolved = false;
      this.node = null;
      this.path = [];
      this.section = null;
    }
  }

  /** The current path if resolved, otherwise an empty array */
  public path: SitemapNode[];

  /** The current sitemap node */
  public node: SitemapNode;

  /** The current section if resolved. */
  public section: SitemapNode;

  /** If true, the route is resolved to current sitemap node. */
  public isResolved: boolean;

  /**
   * Gets logical visibility, as used in the views an UI logic, i.e.:
   *
   * - hidden: Node is null or visibility is Hidden, HiddenReadOnly or Disabled.
   * - readOnly: Node is null or visibility is ReadOnly, HiddenReadOnly.
   * - disabled: Visibility is Disabled.
   * - Visible: Visibility is Visible or null, node is not null.
   * - isFullWidth: The node has an isFullWidth boolean flag set to true.
   *
   * Supports exclamation mark (!) in the beginning to denote negation (true <=> false).
   * @param visibility The visibility value to evaluate for.
   */
  public getVisibility(visibility: SitemapVisibility | string): boolean {
    return SitemapHelper.getVisibility(this.node, visibility);
  }

  /** Returns true if the page should be displayed read-only */
  public get readOnly(): boolean {
    return SitemapHelper.getVisibility(this.node, SitemapVisibility.ReadOnly);
  }

  /**
   * Gets a parameter checking the default value from the schema.
   * The method fails if the parameter is not defined in the schema.
   * @param path Path to the sitemap value / schema definition.
   */
  public getParam(path: string) {
    if (!this.node?.params) {
      throw new Error(`The current node ${this.node?.url} does not have parameters defined (path: '${path}').`);
    }
    const prop = JsonSchemaUtils.getProperty(SitemapHelper.getSchema(this.node), path);
    if (!prop) {
      throw new Error(`The current node ${this.node.url} does not have property ${path} defined in the schema.`);
    }
    const value = Objects.getProperty(this.node, path);
    if (value != null) {
      return value; // Nullish values go to default.
    }
    if (prop.schema.default !== undefined) {
      return prop.schema.default; // Default may be null?
    }
    return null; // Should boolean values default to false?
  }

  /**
   * Checks if the value contain the given flag value.
   * Typically the value is an array of enum strings and this method checks if it contains an item,
   * but the method also checks for a direct match (without being an array).
   * @param path Path to the sitemap value / schema definition.
   * @param flagValue Text value (typically a string enum) that should be present in the array.
   */
  public getFlag(path: string, flagValue: string): boolean {
    const val = this.getParam(path);
    if (Array.isArray(val)) {
      return !!val.find((x) => x == flagValue);
    }
    return val == flagValue;
  }

}